import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../components/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { tripDetails } from '../data/mockData';

export default function DashboardView() {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
      className="space-y-8"
    >
      {/* Overview Section: What it does */}
      <section className="p-8 rounded-3xl bg-cyan-400/5 border border-cyan-400/20 relative overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <SafeIcon icon={FiIcons.FiInfo} className="text-cyan-400 text-xl" />
            <h2 className="text-sm font-mono text-cyan-400 tracking-[0.3em] uppercase">Overview / Mission Brief</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-2xl font-bold text-white mb-4 leading-tight">RouteMuse is a futuristic travel logistics engine for modern explorers.</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                We solve the fragmentation of travel planning for city breaks and high-stakes work trips. By synthesizing flights, accommodation, and local data into a single "Boarding Pass" interface, we ensure you spend less time scrolling and more time experiencing.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 rounded-2xl bg-white/5 border border-white/10">
                <p className="text-cyan-400 font-bold mb-1 text-lg">0%</p>
                <p className="text-[10px] text-gray-500 uppercase font-mono">Data Friction</p>
              </div>
              <div className="p-4 rounded-2xl bg-white/5 border border-white/10">
                <p className="text-coral-400 font-bold mb-1 text-lg">100%</p>
                <p className="text-[10px] text-gray-500 uppercase font-mono">Offline Ready</p>
              </div>
            </div>
          </div>
        </div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-400/10 blur-[100px] -mr-32 -mt-32"></div>
      </section>

      {/* Boarding Pass Hero Card */}
      <div className="glass-card overflow-hidden relative group">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyan-400 via-coral-400 to-cyan-400 bg-[length:200%_100%] animate-gradient-x"></div>
        <div className="p-8 flex flex-col md:flex-row gap-8 items-center justify-between relative z-10">
          <div className="flex-1 w-full">
            <div className="flex flex-col sm:flex-row justify-between items-start mb-8 gap-4">
              <div>
                <h1 className="text-4xl md:text-6xl font-black tracking-tighter text-white mb-2 leading-none">{tripDetails.destination}</h1>
                <div className="flex items-center gap-3">
                  <p className="text-cyan-400 font-mono text-sm tracking-[0.3em]">{tripDetails.subtitle.toUpperCase()}</p>
                  <div className="h-px w-12 bg-white/20"></div>
                  <p className="text-gray-500 font-mono text-xs uppercase tracking-widest">{tripDetails.dates}</p>
                </div>
              </div>
              <div className="flex flex-col items-start sm:items-end">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-400/10 border border-cyan-400/30 text-cyan-400 text-[10px] font-mono mb-2">
                  <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse"></span>
                  {tripDetails.status}
                </div>
                <p className="font-mono text-gray-500 text-[10px] tracking-widest">SYNC_TERMINAL: OK</p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 border-t border-dashed border-white/10 pt-8">
              <div>
                <p className="text-gray-600 text-[10px] font-mono mb-2 tracking-widest">FLIGHT_ID</p>
                <p className="text-2xl font-mono text-white tracking-tighter">{tripDetails.flight}</p>
              </div>
              <div>
                <p className="text-gray-600 text-[10px] font-mono mb-2 tracking-widest">GATE_ACCESS</p>
                <p className="text-2xl font-mono neon-text-coral tracking-tighter">{tripDetails.gate}</p>
              </div>
              <div>
                <p className="text-gray-600 text-[10px] font-mono mb-2 tracking-widest">SEAT_COORDS</p>
                <p className="text-2xl font-mono text-white tracking-tighter">{tripDetails.seat}</p>
              </div>
              <div>
                <p className="text-gray-600 text-[10px] font-mono mb-2 tracking-widest">CLASS_LEVEL</p>
                <p className="text-2xl font-mono text-white tracking-tighter">{tripDetails.class}</p>
              </div>
            </div>
          </div>

          <div className="hidden md:flex flex-col justify-between items-center border-l border-dashed border-white/10 pl-10 h-full min-h-[200px]">
            <SafeIcon icon={FiIcons.FiSend} className="text-4xl text-gray-700 -rotate-45" />
            <div className="flex flex-col gap-1 items-center opacity-30">
              {[...Array(12)].map((_, i) => (
                <div key={i} className={`bg-white rounded-full ${i % 3 === 0 ? 'w-10 h-0.5' : i % 2 === 0 ? 'w-6 h-1' : 'w-8 h-0.5'}`}></div>
              ))}
            </div>
            <p className="text-[9px] font-mono text-gray-600 rotate-90 whitespace-nowrap mt-4 tracking-[0.5em]">RM-TX-TOKYO-001</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}